import React from "react";
import { makeStyles } from "@mui/styles";
import { MailProDialog } from "views/Pma/MailPro/components/MailProDialog/MailProDialog";
import { MailProDialogHeader } from "views/Pma/MailPro/components/MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "views/Pma/MailPro/components/MailProDialog/MailProDialogBody";
import MailProButton from "views/Pma/MailPro/components/UI/MailProButton";
import { DIALOG_BUTTON_BORDER_RADIUS, DIALOG_BUTTON_MIN_HEIGHT } from "./constants";
import MiniTableProCore from "./MiniTableProCore";

const useStyles = makeStyles({
  dialogPaper: {
    maxWidth: "920px",
    width: "90vw",
    "& .MuiDialogContent-root": {
      padding: "0 !important",
    },
  },
  dialogHeader: {
    backgroundColor: "#4A7396",
    color: "white",
    padding: "4px 24px",
    textAlign: "center",
    width: "100%",
  },
  headerTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  headerSubtitle: {
    fontSize: "16px",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
    "& u": {
      textDecoration: "underline",
    },
  },
  dialogBody: {
    padding: "24px 120px 24px 24px", // right padding so property bar can overflow without horizontal scroll
    maxHeight: "70vh",
    overflowY: "auto",
    overflowX: "hidden",
  },
  tableWrap: {
    maxWidth: "830px",
    width: "100%",
    margin: "0 auto",
  },
  tableContainer: {
    width: "100%",
    overflowX: "hidden",
    overflowY: "visible",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontFamily: "objektiv-mk2, sans-serif",
    tableLayout: "fixed",
  },
  tableHeader: {
    backgroundColor: "#f3f4f6",
    "& th": {
      padding: "10px 8px",
      textAlign: "left",
      fontSize: "10px",
      fontWeight: "bold",
      color: "#374151",
      textTransform: "uppercase",
      borderBottom: "2px solid #e5e7eb",
      fontFamily: "objektiv-mk2, sans-serif",
      "& p": {
        margin: 0,
        lineHeight: "1.2",
      },
      "&:first-child": {
        paddingLeft: "13px",
      },
    },
  },
  tableBody: {
    "& td": {
      padding: "10px 8px",
      fontSize: "9.4px",
      color: "#1f2937",
      borderBottom: "1px solid #e5e7eb",
      fontFamily: "objektiv-mk2, sans-serif",
      // Make $ and amount inline in price columns
      "&.list-price-td, &.sale-price-td": {
        "& div": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
          "& p": {
            display: "inline",
            margin: 0,
          },
        },
      },
    },
    "& tr:last-child td": {
      borderBottom: "none",
    },
  },
  editButton: {
    backgroundColor: "#4A7396",
    color: "white",
    fontSize: "11px",
    fontWeight: "bold",
    padding: "4px 12px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontFamily: "objektiv-mk2, sans-serif",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "#3a5a7a",
    },
  },
  footerButtons: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
    position: "relative",
  },
  footerButtonsCenter: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  updateButton: {
    backgroundColor: "#85959C",
    color: "white",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontFamily: "objektiv-mk2, sans-serif",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "#6d7d84",
    },
  },
  cancelButton: {
    backgroundColor: "#85959C",
    color: "white",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    fontFamily: "objektiv-mk2, sans-serif",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "#6d7d84",
    },
  },
});

/**
 * ReviewDialog - Dialog for reviewing properties before adding them to the table
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether dialog is open
 * @param {function} props.onClose - Called when dialog closes
 * @param {string} props.areaName - Area name to display in header (e.g., "CENTRAL MONTECITO")
 * @param {Array} props.properties - Properties to review
 * @param {function} props.onConfirm - Called when UPDATE is clicked, receives the properties array
 * @param {Object} props.marketSaleRowProps - Props to pass to MarketSaleRowPro
 * @param {string|function} props.headerLine1 - First header line text or function that receives propertyCount and returns text
 * @param {string|function} props.headerLine2 - Second header line text or function that receives propertyCount and returns text
 * @param {string} props.updateButtonText - Text for the update button (default: "OK — Everything Looks Correct — UPDATE")
 */
export default function ReviewDialog({
  open,
  onClose,
  onEdit,
  areaName = "",
  properties = [],
  onConfirm,
  marketSaleRowProps = {},
  headerLine1,
  headerLine2,
  updateButtonText = "OK — Everything Looks Correct — UPDATE",
  dialogProps = {},
}) {
  const classes = useStyles();
  const propertyCount = properties.length;

  // Get header line 1 text (default or custom)
  const getHeaderLine1 = () => {
    if (headerLine1) {
      return typeof headerLine1 === 'function' ? headerLine1(propertyCount) : headerLine1;
    }
    return `You are <u>ADDING</u> the following ${propertyCount} propert${propertyCount === 1 ? "y" : "ies"} back to the table.`;
  };

  // Get header line 2 text (default or custom)
  const getHeaderLine2 = () => {
    if (headerLine2) {
      return typeof headerLine2 === 'function' ? headerLine2(propertyCount) : headerLine2;
    }
    return `Please Double Check that the ADDED Property details are correct.`;
  };

  const headerLine1Text = getHeaderLine1();
  const headerLine2Text = getHeaderLine2();
  const hasHTML1 = typeof headerLine1Text === 'string' && /<[^>]+>/.test(headerLine1Text);
  const hasHTML2 = typeof headerLine2Text === 'string' && /<[^>]+>/.test(headerLine2Text);

  const handleConfirm = () => {
    if (onConfirm && properties.length > 0) {
      onConfirm(properties);
    }
    onClose?.();
  };

  return (
    <MailProDialog open={open} onClose={onClose} classes={{ paper: classes.dialogPaper }} {...dialogProps}>
      <MailProDialogHeader>
        <div className={classes.dialogHeader}>
          <div className={classes.headerTitle}>{areaName}</div>
          {hasHTML1 ? (
            <div className={classes.headerSubtitle} dangerouslySetInnerHTML={{ __html: headerLine1Text }} />
          ) : (
            <div className={classes.headerSubtitle}>{headerLine1Text}</div>
          )}
          {hasHTML2 ? (
            <div className={classes.headerSubtitle} dangerouslySetInnerHTML={{ __html: headerLine2Text }} />
          ) : (
            <div className={classes.headerSubtitle}>{headerLine2Text}</div>
          )}
        </div>
      </MailProDialogHeader>
      <MailProDialogBody>
        <div className={`${classes.dialogBody} mini-table-dialog-body`}>
          <div className={classes.tableWrap}>
            <MiniTableProCore
              miniTableProperties={properties}
              printVersion={false}
              editMode={false}
              maxRows={properties.length}
              hideTitleAndSubtitle={true}
              currentArea={{ mlsAreaID: marketSaleRowProps?.mlsAreaID }}
              agentData={marketSaleRowProps?.agentData}
            />
          </div>
        </div>
      </MailProDialogBody>
      <div className={classes.footerButtons}>
        <MailProButton
          variant="outlined"
          label="Cancel / Back"
          onClick={onClose}
          customStyle={{
            fontSize: "12px",
            padding: "6px 14px",
            textTransform: "uppercase",
            fontWeight: "bold",
            minWidth: "auto",
            borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
            minHeight: DIALOG_BUTTON_MIN_HEIGHT,
            height: DIALOG_BUTTON_MIN_HEIGHT,
            boxSizing: "border-box",
          }}
        />
        <div className={classes.footerButtonsCenter}>
          <MailProButton
            variant="filled"
            label={updateButtonText}
            onClick={handleConfirm}
            customStyle={{
              fontSize: "12px",
              padding: "6px 14px",
              textTransform: "uppercase",
              fontWeight: "bold",
              minWidth: "auto",
              borderRadius: DIALOG_BUTTON_BORDER_RADIUS,
              minHeight: DIALOG_BUTTON_MIN_HEIGHT,
              height: DIALOG_BUTTON_MIN_HEIGHT,
              boxSizing: "border-box",
            }}
          />
        </div>
        <div></div>
      </div>
    </MailProDialog>
  );
}
